// src/features/dashboard/components/ManagerDashboard/ProductivityReport/ProductivityByTeamChart.tsx

import { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";

import type { TeamProductivityResponse } from "../../../types/Manager/productivityReport.types";

interface Props {
  data?: TeamProductivityResponse[];
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value?: number | string;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
  if (!active || !payload || payload.length < 2) {
    return null;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white px-2 py-2 shadow-md">
      <div className="mb-1 text-[10px] font-semibold text-slate-800">
        {label}
      </div>

      <div className="flex justify-between text-[10px]">
        <span>This Week</span>
        <span>{payload[0]?.value}%</span>
      </div>

      <div className="flex justify-between text-[10px]">
        <span>Last Week</span>
        <span>{payload[1]?.value}%</span>
      </div>
    </div>
  );
};

const ProductivityByTeamChart = ({ data = [] }: Props) => {
  const [activeSeries, setActiveSeries] = useState<"current" | "previous" | null>(
    null,
  );

  const chartData = Array.isArray(data)
    ? data.map((item) => ({
        team: item.teamLeadName,
        current: item.currentProductivity,
        previous: item.previousProductivity,
      }))
    : [];

  return (
    <div className="rounded-2xl border border-[#D8DEE8] bg-white p-4">
      <div className="mb-3 flex items-center">
        <h3 className="mr-2 text-[13px] font-semibold text-[#003E7E]">
          Productivity by Team
        </h3>

        <span className="rounded-full bg-[#EAF3FF] px-3 py-1 text-[10px] font-medium text-[#2563EB]">
          This week vs last week
        </span>
      </div>

      <div className="mb-3 flex justify-center gap-5 text-[10px]">
        <div
          className="flex cursor-pointer items-center gap-1"
          onMouseEnter={() => setActiveSeries("previous")}
          onMouseLeave={() => setActiveSeries(null)}
        >
          <span className="h-3 w-3 rounded-sm bg-[#A9B7C3]" />
          <span className="text-[#64748B]">Last Week</span>
        </div>

        <div
          className="flex cursor-pointer items-center gap-1"
          onMouseEnter={() => setActiveSeries("current")}
          onMouseLeave={() => setActiveSeries(null)}
        >
          <span className="h-3 w-3 rounded-sm bg-[#284F79]" />
          <span className="text-[#284F79]">This Week</span>
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className="flex h-[220px] items-center justify-center text-sm text-slate-500">
          No data available
        </div>
      ) : (
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 18,
                right: 10,
                left: 0,
                bottom: 10,
              }}
              barGap={2}
              barCategoryGap="18%"
            >
              <CartesianGrid
                vertical={false}
                stroke="#EEF2F6"
                strokeWidth={1}
              />

              <XAxis
                dataKey="team"
                interval={0}
                tick={{
                  fontSize: 10,
                  fill: "#334155",
                }}
                tickMargin={4}
                tickFormatter={(value: string) =>
                  value.length > 14 ? `${value.substring(0, 14)}...` : value
                }
                tickLine={false}
                axisLine={{ stroke: "#D9DDE3" }}
              />

              <YAxis
                domain={[0, 100]}
                ticks={[0, 20, 40, 60, 80, 100]}
                tickFormatter={(value) => `${value}%`}
                width={42}
                tick={{
                  fontSize: 10,
                  fill: "#64748B",
                }}
                tickLine={false}
                axisLine={false}
              />

              <Tooltip
                cursor={{
                  fill: "#F8FAFC",
                }}
                content={<CustomTooltip />}
              />

              <Bar
                dataKey="current"
                name="This Week"
                fill="#284F79"
                fillOpacity={
                  activeSeries === null || activeSeries === "current" ? 1 : 0.25
                }
                radius={[4, 4, 0, 0]}
                barSize={35}
              >
                <LabelList
                  dataKey="current"
                  formatter={(value) => `${value}%`}
                  position="insideBottom"
                  offset={6}
                  fill="#FFFFFF"
                  fontSize={8}
                  fontWeight={700}
                />
              </Bar>

              <Bar
                dataKey="previous"
                name="Last Week"
                fill="#A9B7C3"
                fillOpacity={
                  activeSeries === null || activeSeries === "previous" ? 1 : 0.25
                }
                radius={[4, 4, 0, 0]}
                barSize={35}
              >
                <LabelList
                  dataKey="previous"
                  formatter={(value) => `${value}%`}
                  position="inside"
                  dy={18}
                  fill="#FFFFFF"
                  fontSize={8}
                  fontWeight={700}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ProductivityByTeamChart;
